import React, { useMemo, useState } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";

// Helper: sortare mărimi (numerice crescător, apoi alfabetic XS-XXXL)
const SIZE_RANK = { XS: 1, S: 2, M: 3, L: 4, XL: 5, XXL: 6, XXXL: 7 };
function sortSizes(arr) {
  return [...arr].sort(([a], [b]) => {
    const na = parseFloat(a), nb = parseFloat(b);
    const aN = !isNaN(na), bN = !isNaN(nb);
    if (aN && bN) return na - nb;
    if (aN) return -1;
    if (bN) return 1;
    const ra = SIZE_RANK[String(a).toUpperCase()] || 99;
    const rb = SIZE_RANK[String(b).toUpperCase()] || 99;
    if (ra !== rb) return ra - rb;
    return String(a).localeCompare(String(b));
  });
}

/**
 * PSAModelBreakdown — afișează un tabel compact per model + mărime:
 *  - total bucăți
 *  - cât în stoc (per locație)
 *  - cât la angajați (per persoană)
 * Permite breakdown clar: ce model, ce mărime, unde se află și câte sunt.
 */
export default function PSAModelBreakdown({ items, persons }) {
  const [expanded, setExpanded] = useState(true);

  const personMap = useMemo(() => {
    const m = new Map();
    for (const p of persons || []) m.set(p.id, p);
    return m;
  }, [persons]);

  // structură: model → size → { total, byLocation: {loc:qty}, byPerson: {pid:qty} }
  const breakdown = useMemo(() => {
    const data = new Map();
    for (const it of items) {
      const model = (it.name || "—").trim();
      const size = (it.psa_size || "—").trim();
      const qty = parseFloat(it.quantity);
      const q = isNaN(qty) ? 1 : qty;
      if (!data.has(model)) data.set(model, new Map());
      const sizeMap = data.get(model);
      if (!sizeMap.has(size)) sizeMap.set(size, { total: 0, byLocation: {}, byPerson: {} });
      const cell = sizeMap.get(size);
      cell.total += q;
      if (it.assignee_person_id) {
        cell.byPerson[it.assignee_person_id] = (cell.byPerson[it.assignee_person_id] || 0) + q;
      } else {
        const loc = it.location || "—";
        cell.byLocation[loc] = (cell.byLocation[loc] || 0) + q;
      }
    }
    // Convert to array sorted by total qty descending
    const arr = [];
    for (const [model, sizeMap] of data.entries()) {
      let modelTotal = 0;
      const sizes = [];
      for (const [size, cell] of sizeMap.entries()) {
        modelTotal += cell.total;
        sizes.push([size, cell]);
      }
      arr.push({ model, total: modelTotal, sizes: sortSizes(sizes) });
    }
    arr.sort((a, b) => b.total - a.total);
    return arr;
  }, [items]);

  if (breakdown.length === 0) return null;

  const grandTotal = breakdown.reduce((s, m) => s + m.total, 0);

  return (
    <div className="border-y border-cyan-200 bg-gradient-to-b from-cyan-50/40 to-white" data-testid="psa-model-breakdown">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="w-full flex items-center gap-2 px-4 py-2 text-left hover:bg-cyan-100/40 transition-colors"
        data-testid="psa-breakdown-toggle"
      >
        {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        <span className="text-xs font-bold uppercase tracking-wider text-cyan-900">
          📊 Distribuție pe model & mărime
        </span>
        <span className="text-[11px] text-cyan-700 ml-auto tabular-nums">
          {breakdown.length} model{breakdown.length === 1 ? "" : "e"} · {grandTotal} buc total
        </span>
      </button>
      {expanded && (
        <div className="px-3 pb-3 space-y-2">
          {breakdown.map((m) => (
            <div key={m.model} className="rounded-md border border-cyan-200 bg-white overflow-hidden" data-testid={`psa-breakdown-model-${m.model}`}>
              <div className="px-3 py-1.5 bg-cyan-100/70 flex items-center justify-between gap-2 border-b border-cyan-200">
                <span className="text-sm font-bold text-cyan-900 truncate">👕 {m.model}</span>
                <span className="text-[11px] font-mono font-semibold text-cyan-700 tabular-nums whitespace-nowrap">
                  Total {m.total} buc
                </span>
              </div>
              <div className="divide-y divide-zinc-100">
                {m.sizes.map(([size, cell]) => {
                  const locEntries = Object.entries(cell.byLocation).sort(([, a], [, b]) => b - a);
                  const perEntries = Object.entries(cell.byPerson).sort(([, a], [, b]) => b - a);
                  const stockTotal = locEntries.reduce((s, [, q]) => s + q, 0);
                  const assignedTotal = perEntries.reduce((s, [, q]) => s + q, 0);
                  return (
                    <div key={size} className="px-3 py-2 flex items-start gap-3 flex-wrap" data-testid={`psa-breakdown-size-${m.model}-${size}`}>
                      <div className="flex items-center gap-1.5 flex-shrink-0 min-w-[80px]">
                        <span className="text-[10px] uppercase font-bold text-zinc-500">Mărime</span>
                        <span className="px-2 py-0.5 rounded bg-blue-600 text-white text-xs font-bold tabular-nums">
                          {size}
                        </span>
                        <span className="text-sm font-bold text-zinc-900 tabular-nums">{cell.total}</span>
                      </div>
                      <div className="flex-1 flex flex-wrap gap-1.5 min-w-0">
                        {locEntries.length === 0 && perEntries.length === 0 && (
                          <span className="text-[10px] italic text-zinc-400">fără localizare</span>
                        )}
                        {locEntries.length > 0 && (
                          <div className="flex flex-wrap gap-1 items-center">
                            <span className="text-[10px] uppercase font-bold text-zinc-500 whitespace-nowrap">📦 Stoc ({stockTotal})</span>
                            {locEntries.map(([loc, q]) => (
                              <span
                                key={loc}
                                className="text-[11px] px-1.5 py-0.5 rounded bg-zinc-100 text-zinc-800 font-mono"
                                title={`${q} bucăți în ${loc}`}
                              >
                                {loc}: <strong>{q}</strong>
                              </span>
                            ))}
                          </div>
                        )}
                        {perEntries.length > 0 && (
                          <div className="flex flex-wrap gap-1 items-center">
                            <span className="text-[10px] uppercase font-bold text-emerald-700 whitespace-nowrap">👤 La angajați ({assignedTotal})</span>
                            {perEntries.map(([pid, q]) => {
                              const p = personMap.get(pid);
                              const label = p ? `${p.prenume || p.first_name || ""} ${p.nume || p.last_name || ""}`.trim() || p.full_name || p.name || "—" : pid.slice(0, 6);
                              return (
                                <span
                                  key={pid}
                                  className="text-[11px] px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-900 border border-emerald-200 font-mono"
                                  title={`${q} bucăți la ${label}`}
                                >
                                  {label}: <strong>{q}</strong>
                                </span>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
